import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { DagSourceModel } from "@app/workflow-common/services/DagSourceModel";
import { CommonDataSource } from "@app/workflow-common/services/CommonDataSource";
import { SetCustomFieldBlockModel } from "@app/workflow-common/services/models/SetCustomFieldBlockModel";
import {ActivatedRoute} from "@angular/router";

@Component({
    selector: 'app-set-custom-field-block-widget',
    templateUrl: './set-custom-field-block-widget.component.html',
    styleUrls: ['./set-custom-field-block-widget.component.scss']
})
export class SetCustomFieldBlockWidgetComponent implements OnInit {

    @Input() workflowType: string;
    @Input() objectName: string;
    @Input() blockObject;
    @Input() dagSourceModel: DagSourceModel;
    @Input() screenName: string;
    @Output() close: EventEmitter<{}> = new EventEmitter<{}>();

    constructor(
        private formBuilder: FormBuilder,
        private translate: TranslateService,
        private route: ActivatedRoute
    ) { }

    form: FormGroup;
    blockData: any;
    isLoading: boolean = true;
    isFormSubmitted: boolean = false;
    inputModel: SetCustomFieldBlockModel;
    showDeleteBtn: boolean = false;
    // optgroup structure: [{ label, fields: [{ id, text }] }]
    sourceFields: any[] = [];
    objectCustomFields: any[] = [];
    filteredObjectCustomFields: any[] = [];
    formTemplateId: number;
    targetSelect2Instance: any = undefined;
    showTargetSelect: boolean = true;

    async ngOnInit() {
        this.blockData = this.dagSourceModel.getBlocksSource().getBlockByKey(this.blockObject.key);

        // resolve the form template id (the `:id` route segment) by walking up the route tree
        this.formTemplateId = this.getFormTemplateId();

        // fetch the two dropdown value lists (cached, shared HTTP call) before the panel form loads
        // dropdown 1: current arrive/leave (custom form) template fields, from the workflow PHP API
        await CommonDataSource.getCurrentArriveLeaveTemplateFields(this.screenName, this.formTemplateId).then((fields: any[]) => {
            // date fields are not supported as a mapping source
            const sourceFieldList = (fields || [])
                .filter((field: any) => field.type !== 'date')
                .map((field: any) => ({
                    id: field.col_name,
                    text: field.text,
                    type: field.type,
                    answerType: field.answerType
                }));
            this.sourceFields = sourceFieldList.length
                ? [{ label: 'Source Field', fields: sourceFieldList }]
                : [];
        });
        // dropdown 2: active custom field templates, one optgroup per template
        const customFieldTemplates = await CommonDataSource.getObjectCustomFields(this.screenName);
        this.objectCustomFields = (customFieldTemplates || [])
            .map((template: any) => ({
                label: template.description,
                fields: (template.fields || []).map((field: any) => ({ id: field.col_name, text: field.text, templateId: field.templateId, type: field.type, answerType: field.answerType }))
            }))
            .filter((group: any) => group.fields.length);

        const modelData = this.blockObject['model'] ? this.blockObject['model'] : undefined;
        if (modelData) {
            this.showDeleteBtn = true;
        }
        this.inputModel = new SetCustomFieldBlockModel(modelData);

        // seed the "Custom field to set:" options from the saved source type (edit); empty for a new block
        this.filteredObjectCustomFields = this.filterTargetsByType(this.inputModel.sourceFieldAnswerType);

        this.form = this.formBuilder.group({
            sourceFieldColName: new FormControl(this.inputModel.sourceFieldColName, [Validators.required]),
            sourceDisplayValue: new FormControl(this.inputModel.sourceDisplayValue),
            sourceFieldType: new FormControl(this.inputModel.sourceFieldType),
            sourceFieldAnswerType: new FormControl(this.inputModel.sourceFieldAnswerType),
            targetFieldColName: new FormControl(this.inputModel.targetFieldColName, [Validators.required]),
            targetDisplayValue: new FormControl(this.inputModel.targetDisplayValue),
            targetFieldTemplateId: new FormControl(this.inputModel.targetFieldTemplateId),
            targetFieldType: new FormControl(this.inputModel.targetFieldType),
            targetFieldAnswerType: new FormControl(this.inputModel.targetFieldAnswerType),

            overrideExisting: new FormControl(this.inputModel.overrideExisting),
        });
        this.isLoading = false;
    }

    private getFormTemplateId(): number {
        let route = this.route.snapshot;
        while (route) {
            if (route.params && route.params['id'] != null) {
                return +route.params['id'];
            }
            route = route.parent;
        }
        return this.dagSourceModel.getTemplateId();
    }

    private getDisplayValue(groups: any[], colName: any): any {
        for (const group of groups) {
            const match = group.fields.find((f: any) => f.id == colName);
            if (match) {
                return {
                    text: match.text,
                    type: match.type,
                    answerType: match.answerType,
                    templateId: match.templateId
                };
            }
        }

        return {
            text: '',
            type: '',
            answerType: '',
            templateId: ''
        };
    }

    onSelectSource(event: any = null) {
        const DisplayItem =  this.getDisplayValue(this.sourceFields, event);
        this.form.get('sourceFieldColName').setValue(event);
        this.form.get('sourceDisplayValue').setValue(event ? DisplayItem.text : '');
        this.form.get('sourceFieldType').setValue(event ? DisplayItem.type : '');
        this.form.get('sourceFieldAnswerType').setValue(event ? DisplayItem.answerType : '');
        // dependent "Custom field to set:" — clear it and narrow to fields of the matching type
        this.onSelectTarget(null);
        this.filteredObjectCustomFields = this.filterTargetsByType(event ? DisplayItem.answerType : '');
        this.rerenderTargetSelect();
    }

    // Mirrors isMappingTypeCompatible() in DiaryEventsFieldsQueries.php — keep both in sync,
    // there is no shared source of truth between the Angular and PHP sides.
    private static readonly TYPE_COMPATIBILITY_MAP: { [sourceAnswerType: string]: string[] } = {
        'Input': ['Input', 'Text Area'],
        'Text Area': ['Text Area', 'Input'],
        'Number': ['Input', 'Decimal', 'Number'],
        'Decimal': ['Decimal', 'Number'],
        'Custom calculation': ['Custom calculation'],
        'User': ['Input', 'Text Area', 'User'],
        'Single Checkbox': ['Input', 'Text Area'],
        'Radio': ['Input', 'Text Area'],
        'Toggle': ['Input', 'Text Area'],
        'Dropdown': ['Input', 'Text Area'],
        'Input with Selection Choices': ['Input', 'Text Area'],
        'Checkbox': ['Input', 'Text Area'],
    };

    private filterTargetsByType(sourceAnswerType: any): any[] {
        const allowed = SetCustomFieldBlockWidgetComponent.TYPE_COMPATIBILITY_MAP[sourceAnswerType];
        if (!allowed) {
            return [];
        }
        return (this.objectCustomFields || [])
            .map((group: any) => ({
                label: group.label,
                fields: (group.fields || []).filter((f: any) => allowed.includes(f.answerType))
            }))
            .filter((group: any) => group.fields.length);
    }

    // csSelect2 only reads options at init, so remove and re-add the <select> to re-init it
    private rerenderTargetSelect() {
        if (this.targetSelect2Instance) {
            this.targetSelect2Instance.select2('destroy');
            this.targetSelect2Instance = undefined;
        }
        this.showTargetSelect = false;
        setTimeout(() => { this.showTargetSelect = true; });
    }

    setTargetSelect2Instance(event: any) {
        this.targetSelect2Instance = event;
        // disabled until a "Template field to get:" value is selected
        const hasSource = !!this.form.get('sourceFieldColName').value;
        this.targetSelect2Instance.select2('enable', hasSource);
    }

    onSelectTarget(event: any = null) {
        const DisplayItem =  this.getDisplayValue(this.objectCustomFields, event);
        this.form.get('targetFieldColName').setValue(event);
        this.form.get('targetFieldTemplateId').setValue(event ? DisplayItem.templateId : '');
        this.form.get('targetDisplayValue').setValue(event ? DisplayItem.text : '');
        this.form.get('targetFieldType').setValue(event ? DisplayItem.type : '');
        this.form.get('targetFieldAnswerType').setValue(event ? DisplayItem.answerType : '');
    }

    closeModal() {
        this.close.emit({
            action: 'close',
            data: null
        });
    }

    remove() {
        const blockObject = { ...this.blockObject };
        blockObject.model = this.inputModel;
        this.close.emit({
            action: 'remove_block',
            data: blockObject
        });
    }

    save() {
        this.isFormSubmitted = true;
        const blockObject = { ...this.blockObject };
        const model = new SetCustomFieldBlockModel(this.form.value);
        model.formTemplateId = this.formTemplateId;
        blockObject.model = model;
        this.close.emit({
            action: 'save_and_close',
            data: blockObject
        });
    }
}
